import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../api'
import { ensureExtension } from '../../utils/download'
import './GcsExportDialog.css'

/**
 * GCS Export 모달 다이얼로그 (KBNL-27).
 *
 * Props:
 *   open             — boolean. true 일 때만 렌더 (modal mount/unmount).
 *   onClose          — 사용자 닫기 콜백.
 *   defaultPath      — 시작 GCS 경로 (예: 'gs://kb-agent-tmp/'). 비어있어도 입력으로 시작 가능.
 *   defaultFilename  — 미리 채워줄 파일명 (예: 'my_view.yaml').
 *   defaultExt       — 강제 확장자 (예: 'yaml'). 사용자가 지워도 export 시 보장.
 *   content          — 업로드할 텍스트 (string).
 *   contentType      — MIME ('text/yaml' 기본).
 *   onExported       — 업로드 성공 후 콜백 (result dict 전달).
 *   showToast        — App 의 토스트 함수.
 *   title            — 모달 제목 (기본 'GCS 로 Export').
 *
 * 동작:
 *   - 마운트 시 defaultPath 부터 api.gcsList 호출 → 폴더/파일 표시
 *   - 폴더 클릭 → 드릴다운, [↑ 상위] 클릭 → 부모로
 *   - path bar 직접 편집 → [이동] 으로 임의 gs:// 이동
 *   - 같은 파일명 존재 시 confirm "덮어쓰시겠습니까?"
 */
export default function GcsExportDialog({
  open,
  onClose,
  defaultPath = '',
  defaultFilename = '',
  defaultExt = 'yaml',
  content = '',
  contentType = 'text/yaml',
  onExported,
  showToast,
  title = 'GCS 로 Export',
}) {
  // 현재 탐색 경로 (gs://bucket/prefix/) — 이 값이 바뀌면 자동으로 listing 재로드
  const [currentPath, setCurrentPath] = useState(defaultPath || '')
  // path bar 입력값 (편집 가능, [이동] 누르면 currentPath 로 commit)
  const [pathInput, setPathInput] = useState(defaultPath || '')
  // 파일명 input
  const [filename, setFilename] = useState(ensureExtension(defaultFilename, defaultExt))
  // 서버 listing 상태
  const [listing, setListing] = useState(null)  // {path, parent_path, folders, files}
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // export 진행 상태
  const [uploading, setUploading] = useState(false)

  const filenameInputRef = useRef(null)

  // open=true 로 바뀔 때마다 초기화 (재오픈 시 이전 상태 안 남도록)
  useEffect(() => {
    if (!open) return
    setCurrentPath(defaultPath || '')
    setPathInput(defaultPath || '')
    setFilename(ensureExtension(defaultFilename, defaultExt))
    setError('')
  }, [open, defaultPath, defaultFilename, defaultExt])

  // currentPath 변경 시 listing 재로드
  useEffect(() => {
    if (!open) return
    if (!currentPath) { setListing(null); return }
    let cancelled = false
    setLoading(true); setError('')
    api.gcsList(currentPath)
      .then(res => { if (!cancelled) setListing(res) })
      .catch(e => { if (!cancelled) { setError(e.message || String(e)); setListing(null) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, currentPath])

  // ESC 로 닫기
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // 파일명에 따른 풀 path (export 대상)
  const targetPath = useMemo(() => {
    const p = (currentPath || '').replace(/\/+$/, '') + '/'
    const fn = ensureExtension(filename || '', defaultExt)
    if (!p || p === '/') return ''
    return p + fn
  }, [currentPath, filename, defaultExt])

  // 같은 파일명이 listing 에 이미 있는지
  const willOverwrite = useMemo(() => {
    if (!listing?.files) return false
    const fn = ensureExtension(filename || '', defaultExt).toLowerCase()
    return listing.files.some(f => f.name.toLowerCase() === fn)
  }, [listing, filename, defaultExt])

  const handleEnterFolder = (folder) => {
    setCurrentPath(folder.path)
    setPathInput(folder.path)
  }

  const handleGoUp = () => {
    if (listing?.parent_path) {
      setCurrentPath(listing.parent_path)
      setPathInput(listing.parent_path)
    }
  }

  const handlePathCommit = () => {
    const p = (pathInput || '').trim()
    if (!p) return
    setCurrentPath(p.endsWith('/') ? p : p + '/')
  }

  const handleExport = async () => {
    if (!targetPath) {
      showToast?.('warning', '경로 또는 파일명을 입력하세요.'); return
    }
    if (willOverwrite) {
      if (!window.confirm(`이미 같은 이름의 파일이 있습니다.\n${targetPath}\n덮어쓸까요?`)) return
    }
    setUploading(true)
    try {
      const res = await api.gcsExport({
        path: targetPath,
        content,
        content_type: contentType,
        overwrite: willOverwrite,
      })
      showToast?.('success', `Export 완료 — ${res.path}`)
      onExported?.(res)
      onClose?.()
    } catch (e) {
      // 409 (overwrite=false 일 때 이미 존재) 케이스 — 한 번 더 confirm 후 재시도
      if (/409/.test(e.message || '') || /이미 존재/.test(e.message || '')) {
        if (window.confirm(`이미 같은 이름의 파일이 있습니다. 덮어쓸까요?\n${targetPath}`)) {
          try {
            const res2 = await api.gcsExport({
              path: targetPath, content, content_type: contentType, overwrite: true,
            })
            showToast?.('success', `Export 완료 — ${res2.path}`)
            onExported?.(res2)
            onClose?.()
            return
          } catch (e2) {
            showToast?.('error', `Export 실패: ${e2.message}`)
          }
        }
      } else {
        showToast?.('error', `Export 실패: ${e.message}`)
      }
    } finally {
      setUploading(false)
    }
  }

  if (!open) return null

  return (
    <div className="gcs-modal-backdrop" onClick={onClose}>
      <div className="gcs-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="gcs-modal-head">
          <span className="gcs-modal-title">{title}</span>
          <button className="gcs-close-btn" onClick={onClose} title="닫기">✕</button>
        </div>

        {/* 경로 bar — 직접 편집 가능, 위로 가기 */}
        <div className="gcs-path-bar">
          <button
            className="btn-xs"
            onClick={handleGoUp}
            disabled={!listing?.parent_path || loading}
            title="상위 폴더로"
          >↑ 상위</button>
          <input
            type="text"
            className="gcs-path-input"
            value={pathInput}
            onChange={e => setPathInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handlePathCommit() }}
            placeholder="gs://bucket/prefix/"
          />
          <button className="btn-xs" onClick={handlePathCommit} disabled={loading}>이동</button>
        </div>

        {/* 폴더/파일 리스트 */}
        <div className="gcs-list">
          {loading && <div className="gcs-loading">불러오는 중...</div>}
          {!loading && error && <div className="gcs-error">⚠ {error}</div>}
          {!loading && !error && listing && (
            <>
              {listing.folders.length === 0 && listing.files.length === 0 && (
                <div className="gcs-empty">(비어 있음)</div>
              )}
              {listing.folders.map(f => (
                <div
                  key={f.path}
                  className="gcs-row gcs-row-folder"
                  onDoubleClick={() => handleEnterFolder(f)}
                  onClick={() => handleEnterFolder(f)}
                  title="폴더로 이동"
                >
                  <span className="gcs-row-icon">📁</span>
                  <span className="gcs-row-name">{f.name}/</span>
                </div>
              ))}
              {listing.files.map(f => (
                <div
                  key={f.path}
                  className="gcs-row gcs-row-file"
                  onClick={() => setFilename(f.name)}
                  title="파일명으로 채우기 (덮어쓰기)"
                >
                  <span className="gcs-row-icon">📄</span>
                  <span className="gcs-row-name">{f.name}</span>
                  <span className="gcs-row-meta">{(f.size || 0).toLocaleString()} B</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* 파일명 + 액션 */}
        <div className="gcs-footer">
          <div className="gcs-filename-row">
            <label className="gcs-filename-label">파일명</label>
            <input
              ref={filenameInputRef}
              type="text"
              className="gcs-filename-input"
              value={filename}
              onChange={e => setFilename(e.target.value)}
              placeholder={`view_name.${defaultExt}`}
            />
          </div>
          <div className="gcs-target-preview" title="실제 업로드될 경로">
            업로드 경로: <code>{targetPath || '(경로/파일명 미입력)'}</code>
            {willOverwrite && <span className="gcs-overwrite-warn"> · 같은 이름 존재 (덮어쓰기)</span>}
          </div>
          <div className="gcs-actions">
            <button className="btn-secondary" onClick={onClose} disabled={uploading}>취소</button>
            <button
              className="btn-primary"
              onClick={handleExport}
              disabled={uploading || !targetPath}
              title={!targetPath ? '경로/파일명 필요' : (willOverwrite ? '같은 이름 파일을 덮어씁니다' : '업로드')}
            >{uploading ? 'Export 중...' : 'Export'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}
